import { useState } from 'react';
import { Inbox, Plus } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { usePaginatedQuery } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';
import { useCrm } from './CrmContext';
import { TaskEditor } from './TaskEditor';
import { TaskItem } from './TaskList';
import { buttonClass, inputClass } from './format';
import type { CrmTask } from './types';

type Status = 'open' | 'done' | 'dismissed';
type Kind = 'callback' | 'follow_up' | '';
type Assignee = 'all' | 'me' | 'unassigned' | 'person';
type Due = 'all' | 'overdue' | 'today';

/** The work inbox: callbacks for missed calls and follow-ups, filterable by state, kind and assignee. */
export function CallbacksTab({ ownerFilter = null }: { ownerFilter?: string | null }) {
  const { data, hasFeature } = useCrm();
  const [status, setStatus] = useState<Status>('open');
  const [kind, setKind] = useState<Kind>(ownerFilter !== null ? '' : hasFeature('missedCallRecovery') ? 'callback' : '');
  const [assignee, setAssignee] = useState<Assignee>(ownerFilter === null ? 'all' : ownerFilter ? 'person' : 'unassigned');
  const [due, setDue] = useState<Due>('all');
  const [adding, setAdding] = useState(false);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfTomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime();
  const tasks = usePaginatedQuery<CrmTask>(backendApi.crm.listTasks, { status, kind: kind || undefined, assignee: assignee === 'person' ? 'all' : assignee, assigneeExtension: assignee === 'person' ? ownerFilter : undefined,
    dueAfter: due === 'today' ? startOfToday : undefined,
    dueBefore: due === 'today' ? startOfTomorrow : due === 'overdue' ? startOfToday : undefined }, 50);

  return (
    <Card animate={false}>
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-5">
        <div className="flex gap-1 p-1 rounded-lg bg-gray-100 dark:bg-gray-700/50 w-fit">
          {(['open', 'done', 'dismissed'] as Status[]).map((value) => (
            <button
              type="button"
              key={value}
              onClick={() => setStatus(value)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium ${status === value ? 'bg-white dark:bg-gray-800 shadow text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'}`}
            >
              {value === 'open' ? 'Open' : value === 'done' ? 'Done' : 'Dismissed'}
            </button>
          ))}
        </div>
        <select value={kind} onChange={(event) => setKind(event.target.value as Kind)} className={`${inputClass} lg:w-60`} aria-label="Kind">
          <option value="">Callbacks and follow-ups</option>
          <option value="callback">Callbacks (missed calls)</option>
          <option value="follow_up">Follow-ups</option>
        </select>
        <select value={assignee} onChange={(event) => setAssignee(event.target.value as Assignee)} className={`${inputClass} lg:w-44`} aria-label="Assigned to">
          <option value="all">Everyone's</option>
          <option value="me" disabled={!data?.prefs.extension}>
            Mine{data?.prefs.extension ? ` (${data.prefs.extension})` : ' (set your extension)'}
          </option>
          <option value="unassigned">Unassigned</option>
          {ownerFilter && <option value="person">Extension {ownerFilter}</option>}
        </select>
        <select value={due} onChange={(event) => setDue(event.target.value as Due)} className={`${inputClass} lg:w-44`} aria-label="Due date">
          <option value="all">All due dates</option>
          <option value="overdue">Before today</option>
          <option value="today">Due today</option>
        </select>
        {data?.canManage && (
          <button type="button" onClick={() => setAdding(true)} className={`${buttonClass.primary} lg:ml-auto`}>
            <Plus className="w-4 h-4" /> Follow-up
          </button>
        )}
      </div>

      {!hasFeature('missedCallRecovery') && (
        <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
          Missed-call recovery is not switched on for this account, so missed calls do not become callbacks automatically. Ask InspireTel to switch it on.
        </p>
      )}
      {tasks.error && <p className="text-red-600 text-sm">{tasks.error}</p>}
      {tasks.isLoading && <p className="text-gray-500 text-sm">Loading…</p>}
      {!tasks.isLoading && !tasks.error && tasks.isDone && tasks.items.length === 0 && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <Inbox className="w-10 h-10 mx-auto mb-2 opacity-60" />
          No tasks match these filters.
        </div>
      )}
      <div className="space-y-2">
        {tasks.items.map((task) => (
          <TaskItem key={task.id} task={task} />
        ))}
      </div>
      {!tasks.isLoading && !tasks.error && !tasks.isDone && (
        <button type="button" onClick={tasks.loadMore} disabled={tasks.loadingMore} className={`${buttonClass.secondary} mt-4`}>
          {tasks.loadingMore ? 'Loading…' : 'Load more'}
        </button>
      )}
      {adding && <TaskEditor onClose={() => setAdding(false)} />}
    </Card>
  );
}
